import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlowguardLogo } from "@/components/FlowguardLogo";
import { PUBLIC_SITE_URL } from "@/utils/publicSite";
import { 
  Building2, Mail, Lock, User, ArrowRight, AlertCircle, CheckCircle, 
  Check, Loader2, Shield
} from "lucide-react";
import { toast } from "sonner";

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, verifyEmail, resendVerification } = useAuth();

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Email verification step — the account isn't created until the code is entered.
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [code, setCode] = useState("");

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state]);

  const passwordRequirements = [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Contains a number", met: /\d/.test(password) },
    { label: "Passwords match", met: password === confirmPassword && password.length > 0 },
  ];

  const allRequirementsMet = passwordRequirements.every(req => req.met);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }

    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (!/\d/.test(password)) {
      setError("Password must contain a number");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      await register(name.trim(), email.trim().toLowerCase(), password, "CA", "free");
      setAwaitingCode(true);
      toast.success("We've sent a verification code to your email.");
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Failed to create account. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");

    if (!/^\d{6}$/.test(code.trim())) {
      setError("Please enter the 6-digit code from your email");
      return;
    }

    setLoading(true);

    try {
      await verifyEmail(email.trim().toLowerCase(), code.trim());
      toast.success("Email verified! Welcome to FlowGuard.");
      navigate("/dashboard");
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Could not verify that code. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setLoading(true);

    try {
      await resendVerification(email.trim().toLowerCase());
      toast.success("If a signup is waiting on that email, we've sent a new code.");
    } catch (err) {
      toast.error("Could not resend the code. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const handleGoogleSignup = () => {
    setGoogleLoading(true);
    sessionStorage.setItem("flowguard_google_oauth_intent", "signup");
    window.location.href = `${API}/auth/google/start?intent=signup`;
  };

  return (
    <div className="min-h-screen bg-slate-900 grid lg:grid-cols-2" data-testid="register-page">
      {/* Left Panel - Form */}
      <div className="w-full flex items-center justify-center p-6 lg:p-8 overflow-y-auto z-10">
        <div className="w-full max-w-md">
          {/* Logo */}
          <a href={PUBLIC_SITE_URL} className="flex items-center gap-2 mb-6">
            <FlowguardLogo size="lg" />
          </a>

          {awaitingCode ? (
            <div data-testid="verify-email-step">
              <h1 className="text-3xl font-bold text-white mb-2">Confirm Your Email</h1>
              <p className="text-slate-400 mb-6">
                We sent a 6-digit code to <span className="text-white font-medium">{email.trim().toLowerCase()}</span>.
                Your account is created once you enter it.
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <Label htmlFor="code" className="text-slate-300">Verification Code</Label>
                  <div className="relative mt-1">
                    <CheckCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      className="pl-10 bg-slate-800 border-slate-700 text-white tracking-[0.5em] placeholder:tracking-normal placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-900 font-semibold py-6"
                  disabled={loading || code.length !== 6}
                  data-testid="verify-code-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Verify & Create Account
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center text-slate-400 text-sm mt-6">
                Didn't get it?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="text-amber-500 hover:text-amber-400 font-medium transition-colors disabled:opacity-50"
                >
                  Resend code
                </button>
              </p>
            </div>
          ) : (
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Create Your Account</h1>
            <p className="text-slate-400 mb-6">Join FlowGuard today and start analyzing properties for free.</p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleRegister} className="space-y-4">
              <Button
                type="button"
                onClick={handleGoogleSignup}
                disabled={googleLoading || loading}
                className="w-full bg-white hover:bg-gray-100 text-gray-900 font-medium h-12 border border-gray-300"
                data-testid="google-signup-btn"
              >
                {googleLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-gray-900"></div>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </>
                )}
              </Button>

              <div className="relative mb-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-slate-900 text-slate-500">or continue with email</span>
                </div>
              </div>

              <div>
                <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300">Create Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500"
                    required
                  />
                </div>
              </div>

              {/* Password Requirements */}
              <div className="space-y-2 mt-2 py-1">
                {passwordRequirements.map((req, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    {req.met ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full border border-slate-600" />
                    )}
                    <span className={req.met ? 'text-emerald-400' : 'text-slate-500'}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-900 font-semibold py-6 mt-6"
                disabled={loading || !allRequirementsMet}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    Create Free Account
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-slate-400 text-sm mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </div>
          )}
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_flowguard-realestate/artifacts/a7b9svim_MODERN-FOUR-PLEX-MM-5560-MODERN-STYLE-FRONT-VIEW-scaled.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 60%'
          }}
        >
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Start Building Your Investment Portfolio
          </h2>
          <p className="text-slate-300 text-lg">
            Join thousands of investors using FlowGuard to analyze deals, 
            track performance, and make smarter investment decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
